import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
    User, Leaf, Users, MapPin, Info, HelpCircle, 
    ChevronRight, LogOut, Settings, Eye
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { PushNotificationSettings } from '../components/PushNotificationSettings';

export function MorePage() {
    const navigate = useNavigate();
    const { isAuthenticated, isAdmin, logout, user } = useAuth();
    const { seniorMode, toggleSeniorMode } = useSettings();

    const menuItems = [
        {
            icon: User,
            label: isAuthenticated ? 'My Account' : 'Friend of Burston',
            subtitle: isAuthenticated ? user?.name : 'Login or join',
            path: isAuthenticated ? '/member' : '/login',
            highlight: true,
        },
        {
            icon: Leaf,
            label: 'Garden Tips',
            subtitle: 'Seasonal advice',
            path: '/tips',
        },
        {
            icon: Users,
            label: 'Local Partners',
            subtitle: 'Trusted local businesses',
            path: '/partners',
        },
        {
            icon: MapPin,
            label: 'Visit Burston',
            subtitle: 'Location & opening times',
            path: '/visit',
        },
        {
            icon: Info,
            label: 'About Burston',
            subtitle: 'Our story',
            path: '/about',
        },
        {
            icon: HelpCircle,
            label: 'Help',
            subtitle: 'How to use this app',
            path: '/help',
        },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="page-container" data-testid="more-page">
            <PageHeader title="More" />
            
            <div className="px-4 py-4">
                {/* Menu Items */}
                <div className="space-y-3" data-testid="more-menu">
                    {menuItems.map((item, index) => (
                        <button
                            key={index}
                            onClick={() => navigate(item.path)}
                            className={`w-full card-interactive flex items-center justify-between ${
                                item.highlight ? 'border-2 border-primary/20' : ''
                            }`}
                            data-testid={`menu-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                    item.highlight ? 'bg-primary' : 'bg-primary/10'
                                }`}>
                                    <item.icon className={`w-6 h-6 ${item.highlight ? 'text-white' : 'text-primary'}`} />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-foreground">{item.label}</p>
                                    <p className="text-muted-foreground text-sm">{item.subtitle}</p>
                                </div>
                            </div>
                            <ChevronRight className="w-6 h-6 text-muted-foreground" />
                        </button>
                    ))}
                </div>

                {/* Settings Section */}
                <div className="mt-8">
                    <h2 className="text-lg font-bold text-foreground mb-4">Settings</h2>
                    
                    {/* Senior Friendly Mode */}
                    <div className="card-base" data-testid="senior-mode-setting">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Eye className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <p className="font-bold text-foreground">Large Text Mode</p>
                                    <p className="text-muted-foreground text-sm">Make text easier to read</p>
                                </div>
                            </div>
                            <Switch
                                checked={seniorMode}
                                onCheckedChange={toggleSeniorMode}
                                data-testid="senior-mode-toggle"
                            />
                        </div>
                    </div>
                    
                    {/* Push Notifications */}
                    {isAuthenticated && (
                        <div className="mt-4">
                            <PushNotificationSettings />
                        </div>
                    )}
                </div>

                {/* Admin Link */}
                {isAdmin && (
                    <div className="mt-6">
                        <button
                            onClick={() => navigate('/admin')}
                            className="w-full card-interactive flex items-center justify-between border-2 border-secondary/30"
                            data-testid="admin-link"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                                    <Settings className="w-6 h-6 text-white" />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-foreground">Admin Panel</p>
                                    <p className="text-muted-foreground text-sm">Manage app content</p>
                                </div>
                            </div>
                            <ChevronRight className="w-6 h-6 text-muted-foreground" />
                        </button>
                    </div>
                )}

                {/* Logout */}
                {isAuthenticated && (
                    <div className="mt-6">
                        <button
                            onClick={handleLogout}
                            className="w-full card-base flex items-center justify-center gap-2 text-accent font-bold"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Log Out</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MorePage;
